import React, { InputHTMLAttributes, useState, useEffect } from 'react';
import ReactDom from 'react-dom';
import { Button, Typography } from 'lib/components';
import { Vendor, VendorInput } from 'types';
import { useAddressInput } from 'hooks';
import classes from './index.module.scss';

/**
 * Props interface for the AddVendorModal component
 */
interface Props {
	/** Controls whether the modal is visible */
	isOpen: boolean;
	/** Vendor data for editing (undefined for new vendors) */
	vendor?: Vendor;
	/** Callback function when modal is closed */
	onClose: () => any;
	/** Callback function when create/update action is performed */
	action: (type: 'create' | 'update', data: VendorInput, id?: number) => void;
}

/**
 * Props interface for the TextInput component
 * Extends standard input props with additional styling options
 */
interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
	/** Label text displayed above the input */
	label: string;
	/** Optional margin styling */
	margin?: string;
}

/**
 * TextInput component
 * Renders a labeled input field with customizable styling
 *
 * @param {TextInputProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const TextInput = ({ label, margin, ...props }: TextInputProps) => {
	return (
		<div className={classes['textfield-container']} style={{ margin }}>
			{/* Input label */}
			<Typography variant="h6" color="darkgrey">
				{label}
			</Typography>
			{/* Input field with spread props */}
			<input {...props} />
		</div>
	);
};

/**
 * AddVendorModal component
 * Modal for creating or editing vendors
 * Renders a form with fields for vendor details and address
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if not open
 */
const AddVendorModal = ({ isOpen, onClose, vendor, action }: Props) => {
	// Form state for vendor basic details
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');

	// Custom hook for address input fields with optional pre-filled data
	const {
		addressLine1,
		setAddressLine1,
		addressLine2,
		setAddressLine2,
		city,
		setCity,
		state,
		setState,
		postalCode,
		setPostalCode,
	} = useAddressInput(vendor?.address);

	/**
	 * Initialize form fields when vendor data changes
	 * Populates form with vendor data for editing or resets for new vendors
	 */
	useEffect(() => {
		if (vendor) {
			// Populate form with existing vendor data
			setName(vendor.name);
			setEmail(vendor.email);
			setPhoneNumber(vendor.phoneNumber);
		} else {
			// Reset form for new vendor
			setName('');
			setEmail('');
			setPhoneNumber('');
		}
	}, [vendor]);

	/**
	 * Handle form submission for create or update action
	 * Collects form data and calls the appropriate action callback
	 * Empty string fields are converted to undefined to avoid sending empty values
	 *
	 * @param {string} type - Action type ('create' or 'update')
	 */
	const handleAction = (type: 'create' | 'update') => {
		// Prepare vendor data from form fields
		const vendorData: VendorInput = {
			name,
			email,
			phoneNumber,
			// Convert empty strings to undefined to avoid sending empty values
			addressLine1: addressLine1 === '' ? undefined : addressLine1,
			addressLine2: addressLine2 === '' ? undefined : addressLine2,
			city: city === '' ? undefined : city,
			state: state === '' ? undefined : state,
			postalCode: postalCode === '' ? undefined : postalCode,
		};

		if (type === 'create') {
			// Create new vendor
			action('create', vendorData);
		} else {
			// Update existing vendor
			action('update', vendorData, vendor?.id);
		}
	};

	// Don't render anything if modal is not open
	if (!isOpen) {
		return null;
	}

	// Render modal using React Portal to ensure it appears above other content
	return ReactDom.createPortal(
		<div className={classes['modal']}>
			<div className={classes['vendor-modal-content']}>
				<div>
					{/* Vendor basic information form row */}
					<div className={classes['modal-form']}>
						{/* Vendor name input */}
						<div
							style={{
								flexBasis: '40%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder="Name"
								label="Vendor Name"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>
						{/* Vendor email input */}
						<div
							style={{
								flexBasis: '29%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder="email@example.com"
								label="Email"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
						</div>
						{/* Vendor phone number input */}
						<div
							style={{
								flexBasis: '29%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder=""
								label="Phone Number"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={phoneNumber}
								onChange={(e) => setPhoneNumber(e.target.value)}
							/>
						</div>
					</div>
					{/* Address line 1 and 2 form row */}
					<div className={classes['modal-form']} style={{ marginTop: '1.5rem' }}>
						{/* Address line 1 input */}
						<div
							style={{
								flexBasis: '49%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder="Ave 90940 Pron. LO"
								label="Address Line 1"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={addressLine1}
								onChange={(e) => setAddressLine1(e.target.value)}
							/>
						</div>
						{/* Address line 2 input */}
						<div
							style={{
								flexBasis: '49%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder="Ave 90940 Pron. LO"
								label="Address Line 2"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={addressLine2}
								onChange={(e) => setAddressLine2(e.target.value)}
							/>
						</div>
					</div>
					{/* City, state, and postal code form row */}
					<div className={classes['modal-form']} style={{ marginTop: '1.5rem' }}>
						{/* City input */}
						<div
							style={{
								flexBasis: '30%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder="Ohio"
								label="City"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={city}
								onChange={(e) => setCity(e.target.value)}
							/>
						</div>
						{/* State input */}
						<div
							style={{
								flexBasis: '30%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder="Texas"
								label="State"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={state}
								onChange={(e) => setState(e.target.value)}
							/>
						</div>
						{/* Postal code input */}
						<div
							style={{
								flexBasis: '30%',
								marginBottom: '0.5rem',
							}}
						>
							<TextInput
								placeholder="00000"
								label="Postal Code"
								className={classes['name']}
								style={{
									width: '100%',
								}}
								value={postalCode}
								onChange={(e) => setPostalCode(e.target.value)}
							/>
						</div>
					</div>
				</div>
				{/* Action buttons row */}
				<div className={classes['row']}>
					{/* Submit button - text changes based on create/update mode */}
					<Button
						variant="standard"
						color="primary"
						title={vendor ? 'Update Vendor' : 'Add New Vendor'}
						className={classes['button']}
						style={{
							marginRight: '1rem',
						}}
						onClick={() => handleAction(vendor ? 'update' : 'create')}
					/>
					{/* Cancel button */}
					<Button
						variant="standard"
						color="gray"
						sizing="small"
						title="Cancel"
						onClick={onClose}
						className={classes['button']}
					/>
				</div>
			</div>
		</div>,
		document.getElementById('portal') as HTMLElement
	);
};

export default AddVendorModal;
